import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MediaPicker } from '@/components/feature/MediaPicker';
import { FilePicker } from '@/components/feature/FilePicker';
import { archiveAdminProductFile, createAdminProduct, getAdminProduct, getAdminProductFile, listArchivedProductFiles, permanentlyDeleteProductFile, updateAdminProduct, updateProductDocs, updateProductFeatures, updateProductChangelog, uploadAdminProductFile, type AdminProductFile } from '@/services/products';
import { listCategories, listProductTypes } from '@/services/catalog';
import type { ProductCategory, ProductStatus, ProductType, ProductTypeOption } from '@/domain/types';

const productStatusOptions: ProductStatus[] = ['draft', 'published', 'archived'];
type Tab = 'details' | 'features' | 'changelog' | 'docs';

export default function AdminProductForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [activeTab, setActiveTab] = useState<Tab>('details');

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [type, setType] = useState<ProductType>('plugin');
  const [categoryId, setCategoryId] = useState('');
  const [typeIds, setTypeIds] = useState<string[]>([]);
  const [shortDesc, setShortDesc] = useState('');
  const [description, setDescription] = useState('');
  const [priceFrom, setPriceFrom] = useState('');
  const [isFree, setIsFree] = useState(false);
  const [status, setStatus] = useState<ProductStatus>('draft');
  const [thumbnail, setThumbnail] = useState('');
  const [showThumbnailPicker, setShowThumbnailPicker] = useState(false);
  const [version, setVersion] = useState('1.0.0');
  const [docsHtml, setDocsHtml] = useState('');
  const [docsSaved, setDocsSaved] = useState(false);
  const [featuresHtml, setFeaturesHtml] = useState('');
  const [featuresSaved, setFeaturesSaved] = useState(false);
  const [changelogHtml, setChangelogHtml] = useState('');
  const [changelogSaved, setChangelogSaved] = useState(false);
  const [productFile, setProductFile] = useState<File | null>(null);
  const [showFilePicker, setShowFilePicker] = useState(false);
  const [currentFile, setCurrentFile] = useState<AdminProductFile | null>(null);
  const [fileToArchive, setFileToArchive] = useState<AdminProductFile | null>(null);
  const [archivedFiles, setArchivedFiles] = useState<AdminProductFile[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [productTypes, setProductTypes] = useState<ProductTypeOption[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    async function loadOptions() {
      try {
        const [nextCategories, nextTypes] = await Promise.all([listCategories(), listProductTypes()]);
        setCategories(nextCategories);
        setProductTypes(nextTypes);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unable to load product options');
      }
    }
    loadOptions();
  }, []);

  useEffect(() => {
    if (!id) return;
    let alive = true;

    async function loadProduct() {
      try {
        setLoading(true);
        setError('');
        const product = await getAdminProduct(id);
        if (!alive) return;
        if (!product) { setError('Product not found.'); return; }

        setName(product.name);
        setSlug(product.slug);
        setType(product.type);
        setCategoryId(product.categoryId ?? '');
        setTypeIds(product.typeIds ?? []);
        setShortDesc(product.shortDescription);
        setDescription(product.description);
        setPriceFrom(product.priceFrom.toString());
        setIsFree(product.isFree ?? product.priceFrom <= 0);
        setStatus(product.status);
        setThumbnail(product.thumbnail);
        setVersion(product.version);
        setDocsHtml(product.docsHtml ?? '');
        setFeaturesHtml(product.featuresHtml ?? '');
        setChangelogHtml(product.changelogHtml ?? '');
        const [existingFile, archived] = await Promise.all([
          getAdminProductFile(id),
          listArchivedProductFiles(id),
        ]);
        if (alive) {
          setCurrentFile(existingFile);
          setArchivedFiles(archived);
        }
      } catch (err) {
        if (alive) setError(err instanceof Error ? err.message : 'Unable to load product');
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadProduct();
    return () => { alive = false; };
  }, [id]);

  const handlePermanentDelete = async (file: AdminProductFile) => {
    if (!window.confirm(`Permanently delete "${file.downloadFileName}"? This cannot be undone.`)) return;
    try {
      await permanentlyDeleteProductFile(file);
      setArchivedFiles((prev) => prev.filter((f) => f.id !== file.id));
    } catch (err) {
      const msg = err instanceof Error ? err.message : (err as { message?: string })?.message ?? 'Unable to delete file';
      setError(msg);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !slug) { setError('Product name and slug are required.'); return; }

    try {
      setSaved(true);
      setError('');
      const input = {
        name, slug, type, categoryId, typeIds,
        shortDescription: shortDesc, description,
        priceFrom: isFree ? 0 : Number(priceFrom),
        isFree, status, thumbnail, version,
      };

      const savedProduct = id
        ? await updateAdminProduct(id, input)
        : await createAdminProduct(input);

      if (fileToArchive) await archiveAdminProductFile(fileToArchive.id);
      if (productFile) await uploadAdminProductFile(savedProduct, productFile, isFree ? 'free' : 'purchase');

      navigate('/admin/products');
    } catch (err) {
      const msg = err instanceof Error ? err.message : (err as { message?: string })?.message ?? 'Unable to save product';
      setError(msg);
    } finally {
      setSaved(false);
    }
  };

  const handleSaveDocs = async () => {
    if (!id) return;
    try {
      setDocsSaved(true);
      setError('');
      await updateProductDocs(id, docsHtml);
      setTimeout(() => setDocsSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save documentation');
      setDocsSaved(false);
    }
  };

  const handleSaveFeatures = async () => {
    if (!id) return;
    try {
      await updateProductFeatures(id, featuresHtml);
      setFeaturesSaved(true);
      setTimeout(() => setFeaturesSaved(false), 2000);
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to save.');
    }
  };

  const handleSaveChangelog = async () => {
    if (!id) return;
    try {
      await updateProductChangelog(id, changelogHtml);
      setChangelogSaved(true);
      setTimeout(() => setChangelogSaved(false), 2000);
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to save.');
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-surface-900">{isEdit ? 'Edit Product' : 'New Product'}</h1>
        <p className="text-sm text-surface-500 mt-1">{isEdit ? 'Update product details and settings.' : 'Create a new product for your store.'}</p>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-surface-200">
        {([
          { key: 'details',   label: 'Details' },
          { key: 'features',  label: 'Features HTML' },
          { key: 'changelog', label: 'Changelog HTML' },
          { key: 'docs',      label: 'Documentation' },
        ] as { key: Tab; label: string }[]).map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setActiveTab(key)}
            className={`px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
              activeTab === key
                ? 'border-brand-600 text-brand-600'
                : 'border-transparent text-surface-500 hover:text-surface-700'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="p-5 rounded-xl border border-surface-200 bg-white text-sm text-surface-500">
          Loading product...
        </div>
      )}

      {/* Details Tab */}
      {!loading && activeTab === 'details' && (
        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-6">
          <div className="lg:col-span-2 space-y-5">
            <div className="bg-white rounded-xl border border-surface-200 p-5 md:p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-1.5">Product Name</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" placeholder="Motion Grid Pro" />
              </div>
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-1.5">Slug</label>
                <input type="text" value={slug} onChange={(e) => setSlug(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" placeholder="motion-grid-pro" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-surface-700 mb-1.5">Category</label>
                  <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500">
                    <option value="">Select category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-surface-700 mb-1.5">Types</label>
                  <div className="space-y-2">
                    {productTypes.map((option) => {
                      const checked = typeIds.includes(option.id);
                      return (
                        <label key={option.id} className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-surface-50 cursor-pointer transition-colors">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              const next = checked
                                ? typeIds.filter((id) => id !== option.id)
                                : [...typeIds, option.id];
                              setTypeIds(next);
                              setType(next.length > 0 ? (productTypes.find((t) => t.id === next[0])?.slug as ProductType) || 'plugin' : 'plugin');
                            }}
                            className="w-4 h-4 accent-brand-600 rounded"
                          />
                          <span className="text-sm text-surface-700">{option.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-surface-700 mb-1.5">Price From ($)</label>
                  <input type="number" value={priceFrom} onChange={(e) => setPriceFrom(e.target.value)} disabled={isFree} className="w-full px-3 py-2.5 rounded-lg border border-surface-300 disabled:bg-surface-100 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" placeholder="29" />
                  <label className="mt-2 flex items-center gap-2 text-sm text-surface-600">
                    <input type="checkbox" checked={isFree} onChange={(e) => setIsFree(e.target.checked)} className="w-4 h-4 accent-brand-600" />
                    Free product
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-surface-700 mb-1.5">Version</label>
                  <input type="text" value={version} onChange={(e) => setVersion(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" placeholder="1.0.0" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-surface-700 mb-1.5">Thumbnail URL</label>
                  <div className="flex gap-2">
                    <input type="url" value={thumbnail} onChange={(e) => setThumbnail(e.target.value)} className="flex-1 px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" placeholder="https://..." />
                    <button type="button" onClick={() => setShowThumbnailPicker(true)} className="shrink-0 px-3 py-2 rounded-lg border border-brand-300 bg-brand-50 hover:bg-brand-100 text-brand-600 text-xs font-semibold transition-colors flex items-center gap-1.5 whitespace-nowrap">
                      <i className="ri-image-line" /> Pick
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-surface-700 mb-1.5">Product File</label>
                  {currentFile ? (
                    <div className="flex items-start justify-between gap-3 px-3 py-2.5 rounded-lg border border-surface-300 bg-surface-50 text-sm">
                      <div className="min-w-0">
                        <p className="font-medium text-surface-800 truncate">{currentFile.downloadFileName}</p>
                        <p className="text-xs text-surface-500 mt-0.5">
                          v{currentFile.version}{currentFile.fileSize ? ` · ${Math.round(currentFile.fileSize / 1024)} KB` : ''} · {currentFile.accessLevel}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => { setFileToArchive(currentFile); setCurrentFile(null); }}
                        className="shrink-0 text-xs text-red-600 hover:text-red-700 font-medium"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <>
                      {fileToArchive && (
                        <p className="mb-1.5 text-xs text-amber-600">Current file will be archived on save. Choose a replacement below.</p>
                      )}
                      {productFile ? (
                        <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-brand-300 bg-brand-50 text-sm">
                          <i className="ri-file-3-line text-brand-500 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-surface-800 truncate">{productFile.name}</p>
                            <p className="text-xs text-surface-400">{(productFile.size / 1024).toFixed(1)} KB · ready to upload on save</p>
                          </div>
                          <button type="button" onClick={() => setProductFile(null)} className="shrink-0 text-xs text-red-600 hover:text-red-700 font-medium">Remove</button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setShowFilePicker(true)}
                          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed border-surface-300 hover:border-brand-400 hover:bg-brand-50/30 text-surface-500 hover:text-brand-600 text-sm font-medium transition-colors"
                        >
                          <i className="ri-upload-cloud-2-line text-lg" />
                          Browse or drag & drop a file
                        </button>
                      )}
                    </>
                  )}
                  <p className="mt-1 text-xs text-surface-400">Customers download as slug-version-date.ext.</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-1.5">Short Description</label>
                <input type="text" value={shortDesc} onChange={(e) => setShortDesc(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" placeholder="One-line product summary" />
              </div>
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-1.5">Full Description</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={5} className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500" placeholder="Detailed product description..." />
              </div>
            </div>

            <div className="flex gap-3">
              <button type="submit" disabled={saved} className="px-5 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 disabled:bg-surface-300 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors">
                {saved ? 'Saved!' : isEdit ? 'Save Changes' : 'Create Product'}
              </button>
              <button type="button" onClick={() => navigate('/admin/products')} className="px-5 py-2.5 rounded-lg border border-surface-300 hover:border-surface-400 text-surface-700 text-sm font-medium transition-colors">
                Cancel
              </button>
            </div>
          </div>

          <div className="space-y-5">
            <div className="bg-white rounded-xl border border-surface-200 p-5">
              <h3 className="font-semibold text-surface-900 text-sm mb-4">Publish</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-surface-700 mb-1.5">Status</label>
                  <select value={status} onChange={(e) => setStatus(e.target.value as ProductStatus)} className="w-full px-3 py-2.5 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500">
                    {productStatusOptions.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-surface-200 p-5">
              <h3 className="font-semibold text-surface-900 text-sm mb-4">Thumbnail</h3>
              <div className="border-2 border-dashed border-surface-300 rounded-xl p-6 text-center">
                {thumbnail ? (
                  <img src={thumbnail} alt="" className="w-full aspect-video object-cover rounded-lg" />
                ) : (
                  <>
                    <i className="ri-image-add-line text-2xl text-surface-400 mb-2" />
                    <p className="text-xs text-surface-500">Paste a thumbnail URL in the product details.</p>
                  </>
                )}
              </div>
            </div>
          </div>
        </form>
      )}

      {/* Features Tab */}
      {!loading && activeTab === 'features' && (
        <div className="space-y-4">
          {!isEdit && (
            <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-sm">
              Save the product first before adding features HTML.
            </div>
          )}
          {isEdit && (
            <>
              <div className="bg-white rounded-xl border border-surface-200 overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-surface-200">
                  <div>
                    <h3 className="font-semibold text-surface-900 text-sm">Features HTML</h3>
                    <p className="text-xs text-surface-500 mt-0.5">
                      Full HTML/CSS/JS rendered in the Features tab on the product page.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleSaveFeatures}
                    className="px-4 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-xs font-semibold transition-colors"
                  >
                    {featuresSaved ? '✓ Saved' : 'Save Features'}
                  </button>
                </div>
                <textarea
                  value={featuresHtml}
                  onChange={(e) => setFeaturesHtml(e.target.value)}
                  rows={32}
                  spellCheck={false}
                  className="w-full px-5 py-4 font-mono text-xs text-surface-800 bg-surface-50 focus:outline-none focus:bg-white resize-y"
                  placeholder={`<!DOCTYPE html>\n<html>\n<head>\n  <style>/* your styles */</style>\n</head>\n<body>\n  <!-- features content -->\n</body>\n</html>`}
                />
              </div>
              <p className="text-xs text-surface-400">
                Rendered inside an iframe on the product page Features tab — fully isolated styles and scripts.
              </p>
            </>
          )}
        </div>
      )}

      {/* Changelog Tab */}
      {!loading && activeTab === 'changelog' && (
        <div className="space-y-4">
          {!isEdit && (
            <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-sm">
              Save the product first before adding changelog HTML.
            </div>
          )}
          {isEdit && (
            <>
              <div className="bg-white rounded-xl border border-surface-200 overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-surface-200">
                  <div>
                    <h3 className="font-semibold text-surface-900 text-sm">Changelog HTML</h3>
                    <p className="text-xs text-surface-500 mt-0.5">
                      Full HTML/CSS/JS rendered in the Changelog tab on the product page.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleSaveChangelog}
                    className="px-4 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-xs font-semibold transition-colors"
                  >
                    {changelogSaved ? '✓ Saved' : 'Save Changelog'}
                  </button>
                </div>
                <textarea
                  value={changelogHtml}
                  onChange={(e) => setChangelogHtml(e.target.value)}
                  rows={32}
                  spellCheck={false}
                  className="w-full px-5 py-4 font-mono text-xs text-surface-800 bg-surface-50 focus:outline-none focus:bg-white resize-y"
                  placeholder={`<!DOCTYPE html>\n<html>\n<head>\n  <style>/* your styles */</style>\n</head>\n<body>\n  <!-- changelog entries -->\n</body>\n</html>`}
                />
              </div>
              <p className="text-xs text-surface-400">
                Rendered inside an iframe on the product page Changelog tab — fully isolated styles and scripts.
              </p>
            </>
          )}
        </div>
      )}

      {/* Docs Tab */}
      {!loading && activeTab === 'docs' && (
        <div className="space-y-4">
          {!isEdit && (
            <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-sm">
              Save the product first before adding documentation.
            </div>
          )}
          {isEdit && (
            <>
              <div className="bg-white rounded-xl border border-surface-200 overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-surface-200">
                  <div>
                    <h3 className="font-semibold text-surface-900 text-sm">Documentation HTML</h3>
                    <p className="text-xs text-surface-500 mt-0.5">
                      Paste your full HTML/CSS/JS. Rendered at <code className="font-mono">/docs/{slug}</code>
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {docsHtml && (
                      <a
                        href={`/docs/${slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1.5 rounded-lg border border-surface-300 hover:border-surface-400 text-surface-700 text-xs font-medium flex items-center gap-1.5 transition-colors"
                      >
                        <i className="ri-external-link-line" /> Preview
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={handleSaveDocs}
                      className="px-4 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-xs font-semibold transition-colors"
                    >
                      {docsSaved ? '✓ Saved' : 'Save Docs'}
                    </button>
                  </div>
                </div>
                <textarea
                  value={docsHtml}
                  onChange={(e) => setDocsHtml(e.target.value)}
                  rows={32}
                  spellCheck={false}
                  className="w-full px-5 py-4 font-mono text-xs text-surface-800 bg-surface-50 focus:outline-none focus:bg-white resize-y"
                  placeholder={`<!DOCTYPE html>\n<html>\n<head>\n  <title>Documentation</title>\n  <style>/* your styles */</style>\n</head>\n<body>\n  <!-- your docs content -->\n</body>\n</html>`}
                />
              </div>
              <p className="text-xs text-surface-400">
                The HTML is rendered inside an iframe on the public docs page — your styles and scripts are fully isolated.
              </p>
            </>
          )}
        </div>
      )}

      {isEdit && archivedFiles.length > 0 && activeTab === 'details' && (
        <div className="bg-white rounded-xl border border-surface-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-200">
            <h3 className="font-semibold text-surface-900 text-sm">Archived Files</h3>
            <p className="text-xs text-surface-500 mt-0.5">Old versions — permanently delete the ones you no longer need.</p>
          </div>
          <table className="w-full text-sm">
            <tbody>
              {archivedFiles.map((file) => (
                <tr key={file.id} className="border-b border-surface-50 last:border-b-0">
                  <td className="px-5 py-3">
                    <p className="font-medium text-surface-800">{file.downloadFileName}</p>
                    <p className="text-xs text-surface-500 mt-0.5">
                      v{file.version}{file.fileSize ? ` · ${Math.round(file.fileSize / 1024)} KB` : ''} · {file.accessLevel}
                    </p>
                  </td>
                  <td className="px-5 py-3 text-xs text-surface-400 whitespace-nowrap">
                    {new Date(file.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => handlePermanentDelete(file)}
                      className="px-3 py-1.5 rounded-lg hover:bg-red-50 text-red-600 text-xs font-medium"
                    >
                      Delete permanently
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showThumbnailPicker && (
        <MediaPicker
          selected={thumbnail}
          title="Choose Product Thumbnail"
          onSelect={(url) => setThumbnail(url)}
          onClose={() => setShowThumbnailPicker(false)}
        />
      )}

      {showFilePicker && (
        <FilePicker
          title="Choose Product File"
          onPick={(result) => {
            if (result instanceof File) {
              setProductFile(result);
            } else {
              // Asset from library — wrap in a File-like object for display
              // actual upload happens via handleSubmit
              setProductFile(new File([], result.asset.displayName, { type: result.asset.mimeType ?? '' }));
            }
            setShowFilePicker(false);
          }}
          onClose={() => setShowFilePicker(false)}
        />
      )}
    </div>
  );
}
